// Plan 08-07 — $locale persistent atom + navigator.language detection.
//
// REQ-localization: locale persists across reload (DEC-026 lock — en + sv
// only). Mirrors the same store family as $bearer, $themeKey, $motion (per
// CONTEXT auth-persistence design): localStorage-backed, with a defensive
// decoder that reverts unknown values to the detected default.
//
// Locale resolution chain:
//   1. localStorage `runegambit.locale` (user override) — wins if valid
//   2. navigator.language `sv*` → 'sv', else → 'en' (detectLocale)
//   3. fallback 'en' for SSR / no-navigator environments
//
// This atom is the SINGLE source of truth for the active locale; the
// IntlContextProvider subscribes to it and reloads message dicts on change.

import { persistentAtom } from '@nanostores/persistent'

export const LOCALES = ['en', 'sv'] as const
export type Locale = (typeof LOCALES)[number]

const DEFAULT_LOCALE: Locale = 'en'

function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'sv'
}

/**
 * Detect the preferred locale from `navigator.language`. Returns 'sv' iff
 * the language tag starts with 'sv' (covers sv-SE, sv-FI). Otherwise 'en'.
 * Returns 'en' when navigator is unavailable (SSR / Worker contexts).
 */
export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE
  const lang = (navigator.language ?? '').toLowerCase()
  return lang.startsWith('sv') ? 'sv' : 'en'
}

export const $locale = persistentAtom<Locale>(
  'runegambit.locale',
  detectLocale(),
  {
    encode: (v) => v,
    decode: (s) => (isLocale(s) ? s : detectLocale()),
  },
)
